import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

import { useApi } from '../../api/ApiContext'
import { APP_VERSION } from '../../lib/version'
import { readCachedMessages, readDismissed, writeCachedMessages, writeDismissed } from './messages'

/** Shared with `useSync.ts`'s `usePush`, which invalidates this after a successful push ("and after sync",
 * docs/plan/notifications.md "In-app"). */
export const MESSAGES_QUERY_KEY = ['messages'] as const

/**
 * `GET /messages` on load (and after a push settles), cached in localStorage so it still works offline
 * (docs/plan/notifications.md "In-app"). Dismissals are this hook's own local state, backed by the same storage,
 * so every mounted banner/card sees the same set without re-reading localStorage on every render.
 */
export function useMessages() {
  const api = useApi()
  const query = useQuery({
    queryKey: MESSAGES_QUERY_KEY,
    queryFn: () => api.getMessages(APP_VERSION),
    initialData: () => readCachedMessages() ?? undefined,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (query.data) writeCachedMessages(query.data)
  }, [query.data])

  const [dismissed, setDismissed] = useState(readDismissed)
  const dismiss = useCallback((id: string) => {
    setDismissed((current) => {
      if (current.has(id)) return current
      const next = new Set(current).add(id)
      writeDismissed(next)
      return next
    })
  }, [])

  return { messages: query.data ?? [], dismissed, dismiss }
}
